import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { get_usage } from "../../../../../features/organization/orgSlice";
import Navbar from "./Navbar";
import SummaryCards from "./Components/SummaryCards";
import BotPerformanceTable from "./Components/BotPerformanceTable";
import BotDetailsAccordion from "./Components/BotDetailsAccordion";
import BotFilter from "./Components/BotFilter";
import BotCostHeatmap from "./Components/BotCostHeatmap";
import { BarGraphSkeleton, NavbarSkeleton } from "./Skeletons";
import {
  stackedBarsContainer,
  toggleButtonsGroupLayout,
  usageContainer,
} from "./UsageStyle";
import colors from "../../../../../Constants/colors";
import StackedBarGraph from "./StackedBarGraph";
import CreditUsageDashboard from "./CreditsUsageDashboard/CreditsUsageDashboard";

const Usage = () => {
  const [isExperiments, setIsExperiments] = useState(true);
  const [viewType, setViewType] = useState("qlp");
  const organization = useSelector((state) => state.organization);
  const dispatch = useDispatch();
  const [date, setDate] = useState(new Date());
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [transformedData, setTransformedData] = useState(null);
  const [displayData, setDisplayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBot, setSelectedBot] = useState("");
  const [error, setError] = useState(null);

  const org_id = organization?.organization?._id?.["$oid"];
  const usage_id = organization?.organization?.usage?.[0]?.["$oid"] || null;

  useEffect(() => {
    if (org_id) {
      setLoading(true);
      setError(null); // Reset error before fetching
      if (usage_id && Object.keys(organization?.usage)?.length === 0) {
        dispatch(get_usage({ org_id, usage_id }))
          .unwrap()
          .then(() => {
            setLoading(false);
          })
          .catch((err) => {
            setError("Failed to fetch usage data.");
            setLoading(false);
          });
      } else {
        setError("No usage data available.");
        setLoading(false);
      }
    }
  }, [org_id, usage_id, dispatch]);

  useEffect(() => {
    if (organization.usage && Object.keys(organization.usage).length > 0) {
      const rawData = organization.usage;
      setRawData(rawData);

      const transformedData = transformDataForCurrentMonth(rawData);
      setTransformedData(transformedData);

      const { minDate: mind, maxDate: maxd } = getMinMaxDates(rawData);
      setMinDate(mind);
      setMaxDate(maxd);
    } else {
      setError("Usage data is empty or unavailable.");
    }
  }, [organization.usage]);

  useEffect(() => {
    if (transformedData) {
      setDisplayData(
        isExperiments ? transformedData.experiment : transformedData.deployed
      );
    }
  }, [isExperiments, transformedData]);

  const toggleView = () => {
    setIsExperiments((prev) => !prev);
  };

  const getMinMaxDates = (usage) => {
    const monthYearKeys = Object.keys(usage);

    const dates = monthYearKeys.map((key) => {
      const [year, month] = key.split("_");
      const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
      return new Date(year, monthIndex, 1);
    });

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    return { minDate, maxDate };
  };

  const getCurrentMonthYear = () => {
    // const date = new Date();
    const currentMonth = date?.toLocaleString("default", { month: "long" });
    const currentYear = date?.getFullYear();
    const currentMonthYear = `${currentYear}_${currentMonth}`;
    return currentMonthYear;
  };

  const handleViewTypeChange = (event, newViewType) => {
    if (newViewType !== null) {
      setViewType(newViewType);
    }
  };

  const transformDataForCurrentMonth = (rawData) => {
    // Get the current month and year in the format used in rawData (e.g., "2024_August")
    // const date = new Date();
    const currentMonth = date.toLocaleString("default", { month: "long" });
    const currentYear = date.getFullYear();
    const currentMonthYear = `${currentYear}_${currentMonth}`;

    // Check if the current month-year data exists in rawData
    if (!rawData[currentMonthYear]) {
      return {
        experiment: { user: [], qlp: [] },
        deployed: { user: [], qlp: [] },
      };
    }

    // Get the bot data for the current month
    const monthData = rawData[currentMonthYear];

    // Initialize the transformed data structure
    const transformedData = {
      experiment: { user: [], qlp: [] },
      deployed: { user: [], qlp: [] },
    };

    // Iterate over each bot's data and transform it
    Object.values(monthData.bots).forEach((bot, index) => {
      const botName = bot.bot_name;

      // Extract experiment and deployed data
      if (bot.Experiment?.User) {
        transformedData.experiment.user.push({
          botName: botName,
          "FT Inference Cost ($)":
            bot?.Experiment?.User?.inference_cost?.ft_inference || 0,
          "Foundation Inference Cost ($)":
            bot?.Experiment?.User?.inference_cost?.foundation_inference || 0,
          "Finetune Cost ($)": bot?.Experiment?.User?.finetune_cost || 0,
          "Pinecone Cost ($)": bot?.Experiment?.User?.pinecone_cost || 0,
          "Eval Cost ($)": bot?.Experiment?.User?.eval_cost || 0,
          "Embedding Cost ($)": bot?.Experiment?.User?.embedding_cost || 0,
          "Data Generation Cost ($)":
            bot?.Experiment?.User?.data_generation_cost || 0,
          "Pinecone Storage Cost ($)":
            bot?.Experiment?.User?.pinecone_storage_cost || 0,
        });
      }

      if (bot.Experiment?.QLP) {
        transformedData.experiment.qlp.push({
          botName: botName,
          "FT Inference Cost ($)":
            bot?.Experiment?.QLP?.inference_cost?.ft_inference || 0,
          "Foundation Inference Cost ($)":
            bot?.Experiment?.QLP?.inference_cost?.foundation_inference || 0,
          "Finetune Cost ($)": bot?.Experiment?.QLP?.finetune_cost || 0,
          "Pinecone Cost ($)": bot?.Experiment?.QLP?.pinecone_cost || 0,
          "Embedding Cost ($)": bot?.Experiment?.QLP?.embedding_cost || 0,
          "Eval Cost ($)": bot?.Experiment?.QLP?.eval_cost || 0,
          "Data Generation Cost ($)":
            bot?.Experiment?.QLP?.data_generation_cost || 0,
          "Server Cost ($)": bot?.Experiment?.QLP?.server || 0,
          "Pinecone Storage Cost ($)":
            bot?.Experiment?.QLP?.pinecone_storage_cost || 0,
        });
      }

      if (bot.Deployed?.User) {
        transformedData.deployed.user.push({
          botName: botName,
          "Pinecone Cost ($)": bot?.Deployed?.User?.pinecone_cost || 0,
          "Embedding Cost ($)": bot?.Deployed?.User?.embedding_cost || 0,
          "Pinecone Storage Cost ($)":
            bot?.Deployed?.User?.pinecone_storage_cost || 0,
        });
      }

      if (bot.Deployed?.QLP) {
        transformedData.deployed.qlp.push({
          botName: botName,
          "Pinecone Cost ($)": bot?.Deployed?.QLP?.pinecone_cost || 0,
          "Embedding Cost ($)": bot?.Deployed?.QLP?.embedding_cost || 0,
          "Server Cost ($)": bot?.Deployed?.QLP?.Server || 0,
          "Pinecone Storage Cost ($)":
            bot?.Deployed?.QLP?.pinecone_storage_cost || 0,
        });
      }
    });
    return transformedData;
  };

  const currentData = displayData ? displayData[viewType] : [];

  // content removed from return
  // <div className="purchase-credits">
  //       <CreditUsageDashboard usedCredits={12} totalCredits={150} />
  //     </div>
  //     <hr id="inputs-hr" />
  //     {error ? (
  //       <Typography variant="h6" sx={{ color: "white" }}>
  //         {error}
  //       </Typography>
  //     ) :
  return (
    <Box sx={usageContainer}>
      {organization?.organization?.subscription !== "starter" && (
        <div className="purchase-credits">
          <CreditUsageDashboard />
        </div>
      )}
      {loading ? (
        <NavbarSkeleton />
      ) : (
        <Navbar
          isExperiments={isExperiments}
          setIsExperiments={setIsExperiments}
          date={date}
          setDate={setDate}
          minDate={minDate}
          maxDate={maxDate}
        />
      )}

      {loading ? (
        <>
          <BarGraphSkeleton />
        </>
      ) : displayData == null ? (
        <Typography variant="h6" sx={{ color: "white" }}>
          No Usage data available.
        </Typography>
      ) : (
        <>
          <Typography variant="h6" sx={{ color: colors.white }}>
            Monthly Summary
          </Typography>
          {rawData[getCurrentMonthYear()] && (
            <SummaryCards
              totalCharges={rawData[getCurrentMonthYear()]?.total_charges}
              paymentStatus={rawData[getCurrentMonthYear()]?.payment_status}
              latestInvocation={
                rawData[getCurrentMonthYear()]?.latest_invocation
              }
              remainingCredits={organization?.organization?.credit || 0}
            />
          )}

          {currentData !== undefined && currentData?.length > 0 && (
            <ToggleButtonGroup
              color="primary"
              value={viewType}
              exclusive
              onChange={handleViewTypeChange}
              sx={{ mt: 5, mb: 1, ...toggleButtonsGroupLayout }}
            >
              <ToggleButton value="qlp">Queryloop</ToggleButton>
              <ToggleButton value="user">User</ToggleButton>
            </ToggleButtonGroup>
          )}

          {currentData !== undefined && currentData?.length > 0 && (
            <Box sx={stackedBarsContainer}>
              <StackedBarGraph data={currentData} />
            </Box>
          )}

          {currentData && currentData?.length > 0 && (
            <BotFilter
              bots={currentData}
              selectedBot={selectedBot}
              setSelectedBot={setSelectedBot}
            />
          )}
          {currentData && currentData?.length > 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: colors.white }}>
                  Bot Costs Table
                </Typography>
                <BotPerformanceTable
                  bots={currentData.filter(
                    (bot) => !selectedBot || bot.botName === selectedBot
                  )}
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ color: colors.white }}>
                Cost Heatmap
              </Typography>
              <BotStackedBarChart
                bots={currentData.filter(
                  (bot) => !selectedBot || bot.botName === selectedBot
                )}
              />
            </Grid> */}
            </Grid>
          )}
          {currentData && currentData?.length > 0 && (
            <Box sx={stackedBarsContainer}>
              <Typography variant="h6" sx={{ mt: 4 }}>
                Bot Details
              </Typography>
              <BotDetailsAccordion
                bots={currentData.filter(
                  (bot) => !selectedBot || bot.botName === selectedBot
                )}
              />
            </Box>
          )}
          {currentData && currentData?.length > 0 && (
            <Box sx={stackedBarsContainer}>
              <Typography variant="h6" sx={{ mt: 4 }}>
                Cost Distribution Heatmap
              </Typography>
              <BotCostHeatmap
                bots={currentData.filter(
                  (bot) => !selectedBot || bot.botName === selectedBot
                )}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Usage;
